import {
  FragmentMediaRemoteVideoFragment,
  FragmentMediaVideoFragment,
  FragmentParagraphRemoteVideoFragment,
} from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import clsx from 'clsx';

import { VideoPlayerOverlay } from '@/components/ui/video-player-overlay';

import paragraphStyles from './paragraph.module.scss';

export type RemoteVideoProps = {
  data:
    | FragmentParagraphRemoteVideoFragment
    | FragmentMediaVideoFragment
    | FragmentMediaRemoteVideoFragment;
  className?: string;
  srcSetSizes?: ImageSrcSetSize[];
  aspect?: number;
  id?: string;
};

export const RemoteVideo = ({
  data,
  className,
  srcSetSizes,
  aspect,
  id,
}: RemoteVideoProps) => {
  // MediaRemoteVideo are specifically youtube or vimeo urls.
  // The id needs to be extracted from the url and the platform needs to be determined,
  // The id is then used to construct the iframe src.
  const mediaRemoteSrc =
    data.__typename === 'MediaRemoteVideo'
      ? _extractVideoId(data.mediaOembedVideo ?? '')
      : null;

  let mediaAspect = aspect;
  let autoPlay = false;
  let muted = false;
  let controls = false;
  let overlayText = false;
  let fullWidth = false;

  if (data.__typename === 'ParagraphRemoteVideo') {
    const [width, height] = data.videoAspect.split('_').map((x) => parseInt(x));
    mediaAspect = width / height;
    autoPlay = Boolean(data.autoPlay);
    autoPlay ? (muted = true) : (muted = Boolean(data.muted));
    controls = Boolean(data.controls);
    overlayText = Boolean(data.overlayText);
    fullWidth = Boolean(data.fullWidth);
  } /* else if (data.__typename === 'MediaVideo' || data.__typename === 'MediaRemoteVideo') {
    ! These types do not have an aspect ratio
    const [width, height] = data.mediaAspect.split('_').map((x) => parseInt(x));
    mediaAspect = width / height;
  }*/

  const tailwindSizes = generateResponsiveTailwindSizes(
    srcSetSizes,
    mediaAspect,
    fullWidth
  );
  const iframeSizes = generateIframeContainerSizes(srcSetSizes, mediaAspect);

  return (
    <section
      className={clsx(
        paragraphStyles.paragraph,
        'relative flex flex-col items-center space-y-4',
        className
      )}
      id={id}
    >
      {data.__typename === 'ParagraphRemoteVideo' &&
        data.header &&
        !overlayText && (
          <h2 className={clsx('h1', 'text-center text-primary-dark')}>
            {data.header}
          </h2>
        )}

      {(data.__typename === 'MediaVideo' ||
        data.__typename === 'ParagraphRemoteVideo') && (
        <div className={clsx('relative w-full', tailwindSizes)}>
          {data.__typename === 'ParagraphRemoteVideo' &&
            data.header &&
            overlayText && (
              <h2
                className={clsx(
                  'h1',
                  'absolute left-1/2 top-1/2 z-10 hidden w-full -translate-x-1/2 -translate-y-1/2 p-4 text-center text-white drop-shadow-heading transition-opacity duration-300 lg:p-8 menu:block'
                )}
              >
                {data.header}
              </h2>
            )}

          {controls && data.uuid && (
            <VideoPlayerOverlay
              videoId={data.uuid}
              autoPlay={autoPlay}
              muted={muted}
            />
          )}

          {data.__typename === 'ParagraphRemoteVideo' && (
            <video
              id={data.uuid}
              controls={false}
              muted={!controls ? muted : undefined}
              autoPlay={!controls ? autoPlay : undefined}
              title={data.header ?? 'Remote Video'}
              className="group h-full w-full object-cover"
              preload="metadata"
              playsInline={Boolean(data.playsInline)}
              poster={
                data.image?.__typename === 'MediaImage'
                  ? data.image.mediaImage.url
                  : undefined
              }
              loop={data.loop ?? false}
              controlsList="nodownload"
            >
              <source src={_startSrc(data.url, data.startAt)} />
              <p>
                Your browser does not support video playback.{' '}
                <a href={data.url} target="_blank" rel="noopener noreferrer">
                  Watch it here instead
                </a>
                .
              </p>
            </video>
          )}
          {data.__typename === 'MediaVideo' && (
            <video
              id={data.uuid}
              controls={false}
              title="Remote Video"
              className="h-full w-full object-cover"
              preload="metadata"
              controlsList="nodownload"
            >
              <source src={data.mediaVideoFile.url} />
              <p>
                Your browser does not support video playback.{' '}
                <a
                  href={data.mediaVideoFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch it here instead
                </a>
                .
              </p>
            </video>
          )}
        </div>
      )}
      {data.__typename === 'MediaRemoteVideo' && (
        <div
          className={clsx(iframeSizes.classes, 'relative')}
          style={iframeSizes.style}
        >
          <iframe
            src={
              mediaRemoteSrc?.platform === 'youtube'
                ? `https://www.youtube.com/embed/${mediaRemoteSrc?.id}`
                : `https://player.vimeo.com/video/${mediaRemoteSrc?.id}`
            }
            allow={`picture-in-picture${autoPlay ? '; autoplay' : ''}`}
            allowFullScreen
            style={{ border: 0, width: '100%', height: '100%' }}
          />
        </div>
      )}
    </section>
  );
};

/**
 * @description Start the video at a specific time if startAt is provided
 * @param url - The URL of the video
 * @param startAt - The time to start the video at
 * @returns The URL of the video with the start time
 * @note applies to MediaVideo and ParagraphRemoteVideo
 */
function _startSrc(url: string, startAt: number | null | undefined) {
  return startAt ? `${url}#t=${startAt}` : url;
}

/**
 * @description Extract the video ID from the URL
 * @param url - The URL of the video
 * @returns The video ID and platform
 * @note applies to MediaRemoteVideo
 */
function _extractVideoId(
  url: string
): { platform: 'youtube' | 'vimeo'; id: string } | null {
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );

  if (youtubeMatch) {
    return { platform: 'youtube', id: youtubeMatch[1] };
  }

  const vimeoMatch = url.match(
    /(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/
  );

  if (vimeoMatch) {
    return { platform: 'vimeo', id: vimeoMatch[1] };
  }

  return null;
}
/**
 * Generate responsive tailwind sizes for the video player
 */
function generateResponsiveTailwindSizes(
  sizes?: ImageSrcSetSize[],
  aspect?: number,
  fullWidth?: boolean
): string {
  if (!sizes || !sizes.length) {
    const base = 'w-full';
    if (!fullWidth) return base + ' max-w-[1024px]';
    if (!aspect) return base;
    return `${base} ${getAspectClass(aspect)}`;
  }

  const sortedSizes = [...sizes].sort((a, b) => a.viewport - b.viewport);

  const breakpointClasses = sortedSizes.map((size) => {
    const breakpoint = _getBreakpoint(size.viewport);
    const width = size.image;

    const viewportPercentage = Math.round((width / size.viewport) * 100);
    const widthClass =
      viewportPercentage < 100 ? `w-[${viewportPercentage}vw]` : 'w-full';
    const maxWidthClass = `max-w-[${width}px]`;

    return breakpoint
      ? `${breakpoint}:${widthClass} ${breakpoint}:${maxWidthClass}`
      : `${widthClass} ${maxWidthClass}`;
  });

  const baseClasses = breakpointClasses[0]
    .split(' ')
    .map((cls) => (cls.includes(':') ? cls.split(':')[1] : cls));

  const allClasses = [...baseClasses, ...breakpointClasses.slice(1)];

  if (aspect) {
    allClasses.push(getAspectClass(aspect));
  }

  return allClasses.join(' ');
}

/**
 * Returns the appropriate aspect ratio class based on the given ratio
 */
function getAspectClass(aspect: number): string {
  // Check for common aspect ratios
  if (aspect === 1) return 'aspect-square';
  // Check for 16:9 (approximately 1.7777777777777777)
  if (Math.abs(aspect - 16 / 9) < 0.01) return 'aspect-video';
  // For all other ratios, use custom aspect ratio
  return `aspect-[${Math.round(aspect * 1000) / 1000}]`;
}

/**
 * Generates responsive styles and classes based on srcSizes
 */
function generateIframeContainerSizes(
  sizes?: ImageSrcSetSize[],
  aspect: number = 1
): { classes: string; style: React.CSSProperties } {
  if (!sizes || !sizes.length) {
    return {
      classes: 'w-full max-w-[1024px]',
      style: {
        width: '100%',
        maxWidth: '1024px',
        aspectRatio: aspect || 1,
      },
    };
  }

  const sorted = [...sizes].sort((a, b) => a.viewport - b.viewport);
  const classes: string[] = ['w-full'];

  // Find the largest width for max-width
  const maxWidth = Math.max(...sorted.map((size) => size.image));

  // Create responsive style object
  const style: React.CSSProperties = {
    width: '100%',
    maxWidth: `${maxWidth}px`,
    aspectRatio: aspect || 1,
  };

  // Add responsive max-width classes
  sorted.forEach(({ image, viewport }) => {
    const bp = _getBreakpoint(viewport);
    if (bp) {
      classes.push(`${bp}:max-w-[${image}px]`);
    } else {
      classes.push(`max-w-[${image}px]`);
    }
  });

  return {
    classes: classes.join(' '),
    style,
  };
}

/**
 * Maps a viewport width to the closest Tailwind breakpoint prefix
 * Uses standard Tailwind breakpoints
 */
function _getBreakpoint(viewport: number): string {
  if (viewport >= 1536) return '2xl';
  if (viewport >= 1280) return 'xl';
  if (viewport >= 1024) return 'lg';
  if (viewport >= 768) return 'md';
  if (viewport >= 640) return 'sm';
  return '';
}
